import { z } from 'zod';

/** Акт на дополнительные работы с площадки (ПР-20) */
export const EXTRA_WORK_ACT_STATUSES = {
  draft: 'Ждёт подписи',
  returned: 'Возвращён с замечанием',
  signed: 'Подписан',
} as const;
export type ExtraWorkActStatus = keyof typeof EXTRA_WORK_ACT_STATUSES;

export const EXTRA_WORK_SIGNER_KINDS = {
  customer: 'Заказчик',
  tech_supervision: 'Технадзор',
} as const;
export type ExtraWorkSignerKind = keyof typeof EXTRA_WORK_SIGNER_KINDS;

const DateStr = z.iso.date();

/** Количество и часы — строкой, как в отчёте смены: без округления через числа с плавающей точкой */
const Quantity = z
  .string()
  .regex(/^(0|[1-9]\d{0,14})(\.\d{1,6})?$/, 'Укажите неотрицательное число, не более 6 знаков после запятой');

/** Люди на допработах: профессия, сколько человек, сколько всего человеко-часов */
export const ExtraWorkLabor = z
  .strictObject({
    profession: z.string().trim().min(1).max(200),
    headcount: z.number().int().min(1).max(10000),
    hours: Quantity,
  })
  .superRefine((row, ctx) => {
    if (Number(row.hours) > row.headcount * 24)
      ctx.addIssue({ code: 'custom', message: 'Больше 24 часов на человека', path: ['hours'] });
  });
export type ExtraWorkLabor = z.infer<typeof ExtraWorkLabor>;

/** Техника проекта: машина из списка техники и машино-часы за день */
export const ExtraWorkMachine = z
  .strictObject({ machineId: z.uuid(), hours: Quantity })
  .superRefine((row, ctx) => {
    if (Number(row.hours) > 24)
      ctx.addIssue({ code: 'custom', message: 'Больше 24 часов за день', path: ['hours'] });
  });
export type ExtraWorkMachine = z.infer<typeof ExtraWorkMachine>;

/** Материал из каталога ТМЦ: количество в единице каталога */
export const ExtraWorkMaterial = z
  .strictObject({ itemId: z.uuid(), quantity: Quantity })
  .refine((row) => Number(row.quantity) > 0, { message: 'Количество больше нуля', path: ['quantity'] });
export type ExtraWorkMaterial = z.infer<typeof ExtraWorkMaterial>;

export const ExtraWorkActInput = z.strictObject({
  projectId: z.uuid(),
  objectId: z.uuid().nullable().optional(),
  actDay: DateStr,
  title: z.string().trim().min(3).max(300),
  description: z.string().trim().min(3).max(5000),
  activityId: z.uuid(),
  contractId: z.uuid(),
  laborNote: z.string().trim().max(2000).nullable().optional(),
  machinesNote: z.string().trim().max(2000).nullable().optional(),
  materialsNote: z.string().trim().max(2000).nullable().optional(),
  /** Ресурсы строками (ПР-20): не передано — пусто */
  labor: z.array(ExtraWorkLabor).max(100).optional(),
  machines: z.array(ExtraWorkMachine).max(100).optional(),
  materials: z.array(ExtraWorkMaterial).max(200).optional(),
});
export type ExtraWorkActInput = z.infer<typeof ExtraWorkActInput>;

export const ExtraWorkActSummary = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  projectCode: z.string(),
  number: z.string(),
  actDay: DateStr,
  title: z.string(),
  status: z.enum(Object.keys(EXTRA_WORK_ACT_STATUSES) as [ExtraWorkActStatus, ...ExtraWorkActStatus[]]),
  activityId: z.uuid(),
  activityCode: z.string(),
  activityName: z.string(),
  contractId: z.uuid(),
  contractNumber: z.string(),
  changeId: z.uuid().nullable(),
  changeNumber: z.string().nullable(),
  signedAt: z.string().nullable(),
  signerKind: z
    .enum(Object.keys(EXTRA_WORK_SIGNER_KINDS) as [ExtraWorkSignerKind, ...ExtraWorkSignerKind[]])
    .nullable(),
  canSign: z.boolean(),
  /** Возврат с замечанием: что не так (видно, пока акт не подписан) */
  returnComment: z.string().nullable(),
  returnedAt: z.string().nullable(),
});
export type ExtraWorkActSummary = z.infer<typeof ExtraWorkActSummary>;

export const ExtraWorkActDetail = ExtraWorkActSummary.extend({
  objectId: z.uuid().nullable(),
  objectName: z.string().nullable(),
  description: z.string(),
  laborNote: z.string().nullable(),
  machinesNote: z.string().nullable(),
  materialsNote: z.string().nullable(),
  /** Ресурсы строками с названиями из справочников */
  labor: z.array(z.object({ profession: z.string(), headcount: z.number().int(), hours: z.string() })),
  machines: z.array(
    z.object({ machineId: z.uuid(), name: z.string(), regNumber: z.string().nullable(), hours: z.string() }),
  ),
  materials: z.array(
    z.object({
      itemId: z.uuid(),
      code: z.string(),
      name: z.string(),
      quantity: z.string(),
      unit: z.string(),
    }),
  ),
  signedByName: z.string().nullable(),
  /** Контрольная сумма содержания (sha256): у подписанного — на момент подписи (у старых её нет), у остальных — текущая */
  contentHash: z.string().nullable(),
  returnedByName: z.string().nullable(),
  /** Может ли человек вернуть акт с замечанием (заказчик или технадзор, пока не подписан) */
  canReturn: z.boolean(),
  /** Прораб может править черновик и возвращённый акт */
  canEdit: z.boolean(),
  createdAt: z.string(),
});
export type ExtraWorkActDetail = z.infer<typeof ExtraWorkActDetail>;

export const ExtraWorkActListQuery = z.strictObject({
  day: DateStr.optional(),
  status: z
    .enum(Object.keys(EXTRA_WORK_ACT_STATUSES) as [ExtraWorkActStatus, ...ExtraWorkActStatus[]])
    .optional(),
});
export type ExtraWorkActListQuery = z.infer<typeof ExtraWorkActListQuery>;

export const ExtraWorkActReturn = z.strictObject({
  comment: z.string().trim().min(3).max(2000),
});
export type ExtraWorkActReturn = z.infer<typeof ExtraWorkActReturn>;

/** Правка акта прорабом: тот же состав полей, что при создании, без проекта */
export const ExtraWorkActPatch = ExtraWorkActInput.omit({ projectId: true });
export type ExtraWorkActPatch = z.infer<typeof ExtraWorkActPatch>;

/** Отметка «заказчик подключён к порталу» на проекте (ВК-12) */
export const CustomerConnection = z.object({
  connected: z.boolean(),
  connectedAt: z.string().nullable(),
  connectedByName: z.string().nullable(),
});
export type CustomerConnection = z.infer<typeof CustomerConnection>;
export const CustomerConnectionInput = z.strictObject({ connected: z.boolean() });
export type CustomerConnectionInput = z.infer<typeof CustomerConnectionInput>;

/** Подпись: контрольная сумма того содержания, которое человек видел на экране; при расхождении подпись отклоняется */
export const ExtraWorkActSign = z.strictObject({ expectedHash: z.string().length(64).optional() });
export type ExtraWorkActSign = z.infer<typeof ExtraWorkActSign>;

/** Номер акта: АДР-001 */
export function extraWorkActNumber(seq: number): string {
  return `АДР-${String(seq).padStart(3, '0')}`;
}
